import { RegistrationPointPairs, RegistrationPoints } from '../types/ImagingTypes';

/**
 * Completeness checks for the data a saved layout asserts.
 *
 * These are deliberately pure and event-driven: the wizard calls them when a step
 * is left (so an incomplete step is caught before any layout work is invested) and
 * again before the final save as a backstop. They never look at whether the user
 * touched anything this session -- restored data satisfies them exactly like fresh
 * data does, which is the point: a reused registration must not force a redo.
 */

export const MIN_REGISTRATION_POINTS = 3;

export type EyeCode = 'OD' | 'OS';

/** Structural shape only -- keeps this module usable with legacy persisted JSON. */
export interface RegistrationTransformLike {
  translation?: { x?: number; y?: number };
  rotation?: number;
  scale?: number;
}

export interface RegistrationsLike {
  [eye: string]: { [imgType: string]: RegistrationTransformLike | null } | null | undefined;
}

/** Shape of db.countEach(): { total, OD: { FAF: n, ... }, OS: { ... } }. */
export interface DbStats {
  total: number;
  [eye: string]: number | { [imgType: string]: number } | undefined;
}

const REGISTERED_IMAGE_TYPES = ['FAF', 'FAFONH'] as const;

function imageCount(dbStats: DbStats | null | undefined, eye: EyeCode, imgType: string): number {
  const counts = dbStats?.[eye];
  if (!counts || typeof counts !== 'object')
    return 0;
  return counts[imgType] ?? 0;
}

/**
 * Registration is only expected for image types actually imported for that eye --
 * a participant with no ONH capture must still be able to save.
 */
export function isRegistrationExpected(
  dbStats: DbStats | null | undefined,
  eye: EyeCode,
  imgType: string,
): boolean {
  return imageCount(dbStats, eye, imgType) > 0;
}

/** A pair is only usable when both sides were placed, so count the smaller side. */
export function countPointPairs(pairs: RegistrationPointPairs | null | undefined): number {
  if (!pairs)
    return 0;
  return Math.min(pairs.src?.length ?? 0, pairs.dst?.length ?? 0);
}

/**
 * The identity transform is what saveReg() writes when registration was never
 * performed, so it is the "no registration" marker -- anything else is evidence
 * that a real registration happened, even when no points were persisted.
 */
export function isIdentityTransform(t: RegistrationTransformLike | null | undefined): boolean {
  if (!t)
    return true;
  return (t.translation?.x ?? 0) === 0
    && (t.translation?.y ?? 0) === 0
    && (t.rotation ?? 0) === 0
    && (t.scale ?? 1) === 1;
}

/**
 * `registration_points` was introduced after layouts were already in production,
 * so a layout registered before the cutover carries a valid transform and no
 * points. Those must keep working without forcing a manual re-registration.
 */
export function hasLegacyRegistration(
  registrations: RegistrationsLike | null | undefined,
  eye: EyeCode,
  imgType: string,
): boolean {
  const transform = registrations?.[eye]?.[imgType];
  return !isIdentityTransform(transform);
}

/** Returns the image types whose registration is expected but incomplete. */
export function missingRegistrations(
  registrationPoints: RegistrationPoints | null | undefined,
  dbStats: DbStats | null | undefined,
  eye: EyeCode,
  registrations?: RegistrationsLike | null,
): string[] {
  const missing: string[] = [];
  for (const imgType of REGISTERED_IMAGE_TYPES) {
    if (!isRegistrationExpected(dbStats, eye, imgType))
      continue;
    const pairs = registrationPoints?.[eye]?.[imgType];
    if (countPointPairs(pairs) >= MIN_REGISTRATION_POINTS)
      continue;
    if (hasLegacyRegistration(registrations, eye, imgType))
      continue;
    missing.push(imgType);
  }
  return missing;
}

export function isFovealPitSet(
  fovealPit: { [eye: string]: { scanX?: number } | null } | null | undefined,
  eye: EyeCode,
): boolean {
  const pit = fovealPit?.[eye];
  return !!pit && (pit.scanX ?? 0) > 0;
}

/** Null message = complete. Message names the step to go fix. */
export function checkRegistrationStep(
  registrationPoints: RegistrationPoints | null | undefined,
  dbStats: DbStats | null | undefined,
  eye: EyeCode,
  registrations?: RegistrationsLike | null,
): string | null {
  const missing = missingRegistrations(registrationPoints, dbStats, eye, registrations);
  if (missing.length < 1)
    return null;
  return `Register the ${eye} ${missing.join(' and ')} image${missing.length > 1 ? 's' : ''} `
    + `(at least ${MIN_REGISTRATION_POINTS} point pairs each) before continuing.`;
}

export function checkBScanStep(
  fovealPit: { [eye: string]: { scanX?: number } | null } | null | undefined,
  eye: EyeCode,
): string | null {
  if (isFovealPitSet(fovealPit, eye))
    return null;
  return `Mark the ${eye} foveal pit on the B-scan before continuing.`;
}

export function checkFixationStep(binocularFixation: boolean | null | undefined): string | null {
  if (binocularFixation === null || binocularFixation === undefined)
    return 'Answer the fixation question before continuing.';
  return null;
}

/**
 * What a step's save will persist for one value. The registration editor is
 * authoritative once it has loaded, so its report wins -- including an explicit
 * `null`, which means "the designer invalidated this registration" (e.g. deleted
 * a point pair) and must not silently fall back to the older stored value.
 *
 * `undefined` means the editor never reported at all -- it was never opened, or
 * it is still loading -- and only then does the stored value win. That
 * distinction is what keeps a restored registration from being overwritten by a
 * placeholder while images are still loading.
 */
export function resolveForSave<T>(
  sessionValue: T | null | undefined,
  storedValue: T | null | undefined,
): T | null | undefined {
  return sessionValue === undefined ? (storedValue ?? undefined) : sessionValue;
}

export interface LocalizerSaveState {
  registrationPoints?: RegistrationPoints | null;
  registrations?: RegistrationsLike | null;
  dbStats?: DbStats | null;
  fovealPit?: { [eye: string]: { scanX?: number } | null } | null;
  binocularFixation?: boolean | null;
}

/**
 * Backstop before the final POST. Should never fire in practice -- the per-step
 * checks catch these earlier, where nothing has been invested yet.
 *
 * `steps` is the wizard's own step list, which is the only truthful source of
 * what this protocol asks for: a study-eye-only configuration may omit the
 * fellow eye's registration and B-scan entirely, and omit the fixation question
 * when the protocol fixes it. Demanding work the wizard never offered would
 * block the save with no way for the designer to satisfy it.
 */
export function validateLocalizerSave(state: LocalizerSaveState, steps: string[]): string | null {
  for (const eye of ['OD', 'OS'] as EyeCode[]) {
    if (steps.includes(`register${eye}`)) {
      const reg = checkRegistrationStep(state.registrationPoints, state.dbStats, eye, state.registrations);
      if (reg)
        return reg;
    }
    if (steps.includes(`bscan${eye}`)) {
      const pit = checkBScanStep(state.fovealPit, eye);
      if (pit)
        return pit;
    }
  }
  if (steps.includes('fixationOption'))
    return checkFixationStep(state.binocularFixation);
  return null;
}
